import React from 'react';

const money = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const date = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-GB');
};

const nameOf = (pledge) =>
  [pledge?.customer?.firstName, pledge?.customer?.lastName].filter(Boolean).join(' ') || '—';

const Header = ({ company, title, from, to }) => (
  <div className="sr-header">
    <h2>{company?.companyName || 'MONEY LENDER'}</h2>
    <div>{company?.address || ''}</div>
    <div>
      {company?.mobileNumber ? `Mob.: ${company.mobileNumber}` : ''}
      {company?.licenseNumber ? ` | Licence No.: ${company.licenseNumber}` : ''}
    </div>
    <h3>{title}</h3>
    <div>From {date(from)} To {date(to)}</div>
  </div>
);

const Table = ({ columns, rows, totals, empty = 'No entries found for the selected period.' }) => (
  <table className="sr-table">
    <thead>
      <tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
    </thead>
    <tbody>
      {rows.length === 0 ? (
        <tr><td colSpan={columns.length} className="center">{empty}</td></tr>
      ) : rows.map((row, index) => (
        <tr key={row.id || `${row.date || ''}-${index}`}>
          {columns.map((column) => (
            <td key={column.key} className={column.numeric ? 'numeric' : ''}>
              {column.render ? column.render(row, index) : row[column.key]}
            </td>
          ))}
        </tr>
      ))}
      {totals ? (
        <tr className="sr-total">
          {columns.map((column, index) => (
            <td key={column.key} className={column.numeric ? 'numeric' : ''}>
              {index === 0 ? 'TOTAL' : totals[column.key] ?? ''}
            </td>
          ))}
        </tr>
      ) : null}
    </tbody>
  </table>
);

const REPORT_TITLES = {
  pledges: 'Pledge Created Register',
  releases: 'Release Register',
  interest: 'Interest Collected Account',
  stockBook: 'Money Lending Stock Book',
  cashBook: 'Cash Book',
  monthly: 'Monthly Day-wise Loan Report',
  summary: 'Loan Summary'
};

const ReportBody = ({ reportKey, data }) => {
  const { pledges = [], releases = [], stockRows = [], cashBook = {}, summary = {} } = data;

  if (reportKey === 'pledges') {
    return (
      <Table
        columns={[
          { key: 'date', label: 'Date', render: (r) => date(r.date) },
          { key: 'ticket', label: 'Pledge No.', render: (r) => r.loanDetails?.ticketNo || '—' },
          { key: 'customer', label: 'Customer Name / Narration', render: nameOf },
          { key: 'articles', label: 'Description of Goods', render: (r) => (r.articles || []).map((a) => a.description).filter(Boolean).join(', ') || '—' },
          { key: 'principal', label: 'Loan Amount Given', numeric: true, render: (r) => money(r.loanDetails?.principalAmount) }
        ]}
        rows={pledges}
        totals={{ principal: money(summary.periodLoanGiven) }}
      />
    );
  }

  if (reportKey === 'releases') {
    return (
      <Table
        columns={[
          { key: 'date', label: 'Release Date', render: (r) => date(r.releaseDate) },
          { key: 'releaseNo', label: 'Release No.', render: (r) => r.releaseNo || '—' },
          { key: 'ticketNo', label: 'Pledge No.' },
          { key: 'customer', label: 'Customer Name', render: (r) => r.client?.name || '—' },
          { key: 'principal', label: 'Principal Received', numeric: true, render: (r) => money(r.calculations?.principle) },
          { key: 'interest', label: 'Interest', numeric: true, render: (r) => money(r.calculations?.interest) },
          { key: 'total', label: 'Total Received', numeric: true, render: (r) => money(r.calculations?.total) }
        ]}
        rows={releases}
        totals={{
          principal: money(summary.periodPrincipalReceived),
          interest: money(summary.periodInterestReceived),
          total: money(summary.periodTotalReceived)
        }}
      />
    );
  }

  if (reportKey === 'interest') {
    const rows = releases.filter((r) => Number(r.calculations?.interest || 0) !== 0);
    return (
      <Table
        columns={[
          { key: 'date', label: 'Date', render: (r) => date(r.releaseDate) },
          { key: 'releaseNo', label: 'Voucher No.', render: (r) => r.releaseNo || r.ticketNo || '—' },
          { key: 'ticketNo', label: 'Pledge No.' },
          { key: 'customer', label: 'Customer Name / Narration', render: (r) => r.client?.name || '—' },
          { key: 'months', label: 'Period', render: (r) => `${Number(r.months || 0)} month(s)` },
          { key: 'interest', label: 'Interest Collected', numeric: true, render: (r) => money(r.calculations?.interest) }
        ]}
        rows={rows}
        totals={{ interest: money(summary.periodInterestReceived) }}
      />
    );
  }

  if (reportKey === 'stockBook' || reportKey === 'monthly') {
    return (
      <Table
        columns={[
          { key: 'date', label: 'Date', render: (r) => date(r.date) },
          { key: 'opening', label: 'Opening Loan Balance', numeric: true, render: (r) => money(r.opening) },
          { key: 'loanGiven', label: 'Loan Amount Given', numeric: true, render: (r) => money(r.loanGiven) },
          { key: 'principalReceived', label: 'Loan Amount Received', numeric: true, render: (r) => money(r.principalReceived) },
          ...(reportKey === 'monthly'
            ? [{ key: 'interestReceived', label: 'Interest Received', numeric: true, render: (r) => money(r.interestReceived) }]
            : []),
          { key: 'closing', label: 'Closing Loan Balance', numeric: true, render: (r) => money(r.closing) }
        ]}
        rows={stockRows}
        totals={{
          loanGiven: money(summary.periodLoanGiven),
          principalReceived: money(summary.periodPrincipalReceived),
          interestReceived: reportKey === 'monthly' ? money(summary.periodInterestReceived) : '',
          closing: money(summary.closingOutstanding)
        }}
      />
    );
  }

  if (reportKey === 'cashBook') {
    return (
      <Table
        columns={[
          { key: 'date', label: 'Date', render: (r) => date(r.date) },
          { key: 'document', label: 'Vch. No.', render: (r) => r.entryNo || r.document || r.ticketNo || '—' },
          { key: 'details', label: 'Customer Name / Narration', render: (r) => r.details || r.accountName || '—' },
          { key: 'debit', label: 'Debit', numeric: true, render: (r) => r.debit ? money(r.debit) : '' },
          { key: 'credit', label: 'Credit', numeric: true, render: (r) => r.credit ? money(r.credit) : '' },
          { key: 'balance', label: 'Balance', numeric: true, render: (r) => money(r.balance) }
        ]}
        rows={cashBook.rows || []}
        totals={{
          debit: money(cashBook.totalDebit),
          credit: money(cashBook.totalCredit),
          balance: money(cashBook.closingBalance)
        }}
      />
    );
  }

  const summaryRows = [
    { label: 'Loans outstanding before selected period', amount: summary.openingOutstanding, count: summary.openingCount },
    { label: 'Loan given in selected period', amount: summary.periodLoanGiven, count: summary.pledgeCount },
    { label: 'Total loans', amount: summary.totalLoans, count: summary.totalLoanCount },
    { label: 'Loan principal received on release', amount: summary.periodPrincipalReceived, count: summary.releaseCount },
    { label: 'Interest collected', amount: summary.periodInterestReceived, count: summary.interestCount },
    { label: 'Outstanding at end of selected period', amount: summary.closingOutstanding, count: summary.closingCount }
  ];
  return (
    <Table
      columns={[
        { key: 'label', label: 'Particulars' },
        { key: 'amount', label: 'Amount', numeric: true, render: (r) => `Rs. ${money(r.amount)}` },
        { key: 'count', label: 'No. of Pledges / Receipts', numeric: true }
      ]}
      rows={summaryRows}
    />
  );
};

const StatutoryReportsPrint = ({ dayBookData }) => {
  const data = dayBookData || {};
  const reportKeys = data.visibleReports?.length
    ? data.visibleReports
    : Object.keys(REPORT_TITLES);

  return (
    <div className="statutory-reports-print">
      <style>{`
        .statutory-reports-print { color:#000; font-family: Arial, sans-serif; font-size: 10px; }
        .sr-page { page-break-after: always; min-height: 250mm; }
        .sr-page:last-child { page-break-after: auto; }
        .sr-header { text-align:center; margin-bottom:10px; line-height:1.35; }
        .sr-header h2, .sr-header h3 { margin:2px 0; }
        .sr-table { width:100%; border-collapse:collapse; }
        .sr-table th, .sr-table td { border:1px solid #000; padding:4px 5px; vertical-align:top; }
        .sr-table th { background:#eee; text-align:center; }
        .sr-table .numeric { text-align:right; white-space:nowrap; }
        .sr-table .center { text-align:center; padding:16px; }
        .sr-total { font-weight:700; }
        @media screen { .sr-page { min-height:auto; padding:12px; margin-bottom:16px; border:1px solid #bbb; } }
      `}</style>
      {reportKeys.map((reportKey) => (
        <section className="sr-page" key={reportKey}>
          <Header
            company={data.company}
            title={REPORT_TITLES[reportKey]}
            from={data.from}
            to={data.to}
          />
          <ReportBody reportKey={reportKey} data={data} />
        </section>
      ))}
    </div>
  );
};

export { REPORT_TITLES };
export default StatutoryReportsPrint;
